import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const PWAInstallContext = createContext();

export const usePWAInstall = () => {
  const context = useContext(PWAInstallContext);
  if (!context) {
    throw new Error('usePWAInstall must be used within a PWAInstallProvider');
  }
  return context;
};

const INSTALLED_FLAG = 'pwa-installed';

const detectStandalone = () => {
  if (typeof window === 'undefined') return false;
  const displayStandalone =
    window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = 'standalone' in window.navigator && window.navigator.standalone;
  return Boolean(displayStandalone || iosStandalone);
};

const isIOSDevice = () =>
  typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

export const PWAInstallProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(() => {
    // The inline script in index.html may have captured the event before React
    // mounted; pick it up so the install button isn't lost due to timing.
    if (typeof window === 'undefined') return null;
    return window.__ulcaapp_deferredInstallPrompt || null;
  });
  const [isInstalled, setIsInstalled] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Standalone display is the authoritative runtime signal. The persisted
    // flag is only a hint for the normal-browser-tab case (where an installed
    // app fires no beforeinstallprompt and isn't standalone); it is cleared as
    // soon as the browser tells us the app is installable again (see below).
    return detectStandalone() || localStorage.getItem(INSTALLED_FLAG) === 'true';
  });
  // iOS Safari never fires beforeinstallprompt; offer manual instructions instead.
  const [isIOS] = useState(() => isIOSDevice());

  useEffect(() => {
    // NOTE: we intentionally do NOT early-return when `isInstalled` is true.
    // The listeners must stay attached so that a `beforeinstallprompt` fired
    // after the user uninstalls the PWA can clear the stale "installed" flag
    // and bring the Install button back without clearing browser data.

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the browser's default mini-infobar so we can use our own UI.
      // (The event may originate directly or be re-dispatched from index.html.)
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      // Chrome only fires this when the app is eligible to install, i.e. it is
      // NOT currently installed. Treat it as an authoritative "not installed"
      // signal and drop any stale flag left over from a previous install.
      setIsInstalled(false);
      try {
        localStorage.removeItem(INSTALLED_FLAG);
      } catch (_) {}
      setDeferredPrompt(window.__ulcaapp_deferredInstallPrompt || e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      try {
        localStorage.setItem(INSTALLED_FLAG, 'true');
      } catch (_) {}
    };

    // Native events (in case they fire after we mount)...
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    // ...and the early-capture bridge from index.html.
    window.addEventListener('ulcaapp:beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('ulcaapp:appinstalled', handleAppInstalled);

    // A prompt captured by the inline script means the app is installable
    // (and therefore not installed) — adopt it and clear any stale flag.
    if (window.__ulcaapp_deferredInstallPrompt) {
      setIsInstalled(false);
      try {
        localStorage.removeItem(INSTALLED_FLAG);
      } catch (_) {}
      setDeferredPrompt(window.__ulcaapp_deferredInstallPrompt);
    }

    // Re-check standalone in case the app was launched as an installed PWA.
    if (detectStandalone() || window.__ulcaapp_pwaInstalled) {
      handleAppInstalled();
    }

    // When the user returns to the tab (e.g. after uninstalling the PWA from
    // the OS), re-evaluate install state so a stale flag doesn't keep the
    // Install button hidden. detectStandalone() is the source of truth.
    const recheckInstallState = () => {
      if (detectStandalone()) {
        setIsInstalled(true);
      } else {
        let hasFlag = false;
        try {
          hasFlag = localStorage.getItem(INSTALLED_FLAG) === 'true';
        } catch (_) {}
        if (!hasFlag) setIsInstalled(false);
      }
    };
    document.addEventListener('visibilitychange', recheckInstallState);
    window.addEventListener('focus', recheckInstallState);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('ulcaapp:beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('ulcaapp:appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', recheckInstallState);
      window.removeEventListener('focus', recheckInstallState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const promptInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // Once used, the prompt cannot be reused.
      setDeferredPrompt(null);
      try {
        window.__ulcaapp_deferredInstallPrompt = null;
      } catch (_) {}
      if (outcome === 'accepted') {
        setIsInstalled(true);
        try {
          localStorage.setItem(INSTALLED_FLAG, 'true');
        } catch (_) {}
      }
      return outcome;
    }

    if (isIOS) {
      alert(
        'To install this app on your iOS device:\n\n1. Tap the Share button\n2. Tap "Add to Home Screen"\n3. Tap "Add"'
      );
      return 'instructions';
    }

    // No native prompt available (event not fired yet, unsupported browser,
    // or served without an installable context). Give the user manual steps so
    // tapping the drawer item always does something useful.
    alert(
      'To install this app:\n\n' +
        '• Android (Chrome): open the browser menu (⋮) and tap "Install app" / "Add to Home screen".\n' +
        '• Desktop (Chrome/Edge): click the install icon in the address bar, or use the browser menu → "Install UlcaApp".\n\n' +
        'If you don\'t see the option yet, refresh the page and try again.'
    );
    return 'instructions';
  }, [deferredPrompt, isIOS]);

  // A single source of truth for whether an install option should be offered at all.
  const canInstall = !isInstalled && (Boolean(deferredPrompt) || (isIOS && !detectStandalone()));

  const value = {
    canInstall,
    isInstalled,
    isIOS,
    promptInstall,
  };

  return <PWAInstallContext.Provider value={value}>{children}</PWAInstallContext.Provider>;
};

export default PWAInstallContext;
